import { put, takeEvery } from 'redux-saga/effects';
import type { Saga } from 'redux-saga';
import { actions as toastrActions } from 'react-redux-toastr';
import type { Action } from '../App/types';
import {
  HANDLE_HUD_RECALC_SUCCESS,
  HANDLE_HUD_RECALC_FAILURE,
  MINIMUM_RENT_IN_PROGRESS_TOAST_ID,
  MINIMUM_RENT_RESULT_TOAST_ID,
} from './constants';
import {
  clearMinimumRentFallback,
  getMinimumRentCorrelationId,
} from './minimumRentFallback';

/**
 * Idempotency safeguard. Even with the socket listener registered a single
 * time, defend against the same HUD recalc notification being handled twice
 * (e.g. a duplicate broadcast) by remembering the correlationIds already
 * processed. Both success and failure share this registry so a correlationId is
 * acted on exactly once. The set is bounded so it never leaks memory.
 */
const handledCorrelationIds = new Set();
const MAX_HANDLED_CORRELATION_IDS = 200;

export const resetHandledHudCorrelationIds = () => {
  handledCorrelationIds.clear();
};

const isCorrelationHandled = (correlationId: ?string): boolean =>
  !!correlationId && handledCorrelationIds.has(correlationId);

const markCorrelationHandled = (correlationId: ?string) => {
  if (!correlationId) {
    return;
  }
  if (handledCorrelationIds.size >= MAX_HANDLED_CORRELATION_IDS) {
    const oldest = handledCorrelationIds.values().next().value;
    handledCorrelationIds.delete(oldest);
  }
  handledCorrelationIds.add(correlationId);
};

/**
 * Decides whether a HUD recalc notification should act on the in-progress
 * toast.
 *
 * - Without a propertyId we cannot target a toast id, so we never touch the
 *   in-progress toast (avoids acting on the literal id `...:undefined`).
 * - When an expected correlationId is registered for the property, the
 *   notification must match it; otherwise it belongs to a different save
 *   (concurrent save on the same property, another tab) and is ignored.
 * - When no expected correlationId is registered (e.g. a page reload dropped the
 *   in-memory registry) we fall back to matching by propertyId so the happy
 *   path keeps working.
 */
const shouldHandleHudRecalc = (
  propertyId: ?string,
  correlationId: ?string,
): boolean => {
  if (!propertyId) {
    return false;
  }

  const expectedCorrelationId = getMinimumRentCorrelationId(propertyId);
  if (expectedCorrelationId && expectedCorrelationId !== correlationId) {
    return false;
  }

  return true;
};

export function* handleHudRecalcSuccess(action: Action<any>): Saga<void> {
  const { propertyId, correlationId, title, message } = action.payload;

  if (!shouldHandleHudRecalc(propertyId, correlationId)) {
    return;
  }

  if (isCorrelationHandled(correlationId)) {
    return;
  }
  markCorrelationHandled(correlationId);

  clearMinimumRentFallback(propertyId);

  yield put(
    toastrActions.remove(`${MINIMUM_RENT_IN_PROGRESS_TOAST_ID}:${propertyId}`),
  );

  yield put(
    toastrActions.add({
      id: `${MINIMUM_RENT_RESULT_TOAST_ID}:${propertyId}`,
      type: 'success',
      title,
      message,
      options: {
        showCloseButton: true,
        removeOnHover: true,
      },
    }),
  );
}

export function* handleHudRecalcFailure(action: Action<any>): Saga<void> {
  const { propertyId, correlationId, title, message } = action.payload;

  if (!shouldHandleHudRecalc(propertyId, correlationId)) {
    return;
  }

  if (isCorrelationHandled(correlationId)) {
    return;
  }
  markCorrelationHandled(correlationId);

  clearMinimumRentFallback(propertyId);

  yield put(
    toastrActions.remove(`${MINIMUM_RENT_IN_PROGRESS_TOAST_ID}:${propertyId}`),
  );

  yield put(
    toastrActions.add({
      id: `${MINIMUM_RENT_RESULT_TOAST_ID}:${propertyId}`,
      type: 'error',
      title,
      message,
      options: {
        showCloseButton: true,
        removeOnHover: true,
      },
    }),
  );
}

export function* handleHudRecalcSuccessSaga(): Saga<void> {
  yield takeEvery(HANDLE_HUD_RECALC_SUCCESS, handleHudRecalcSuccess);
}

export function* handleHudRecalcFailureSaga(): Saga<void> {
  yield takeEvery(HANDLE_HUD_RECALC_FAILURE, handleHudRecalcFailure);
}

export default [handleHudRecalcSuccessSaga, handleHudRecalcFailureSaga];
